'use client';

import { createRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import {
  AudienceSection,
  BuiltForAISection,
  ConsultingTeaserSection,
  FeatureShotsSection,
  FinalCTASection,
  HeroSection,
  HowItWorksSection,
  LanguagesSection,
  OriginSection,
  OutcomeSection,
  PhilosophySection,
  PricingTeaserSection,
  ProblemSection,
  ReviewSection,
} from '@/components/sections';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { seoHead } from '@/lib/seo/routeMeta';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  head: () => seoHead('/'),
  validateSearch: (
    search: Record<string, unknown>
  ): { installation_id?: string } => ({
    installation_id: (search.installation_id as string) || undefined,
  }),
  component: Home,
});

const GITHUB_PR_SHOTS = [
  {
    assetKey: 'screenshotPrComment' as const,
    caption: 'A summary of what to fix',
    hidden: true,
  },
  {
    assetKey: 'screenshotAnnotation' as const,
    caption: 'A swallowed error, caught inline',
  },
  {
    assetKey: 'screenshotCheckRun' as const,
    caption: 'Pass or fail, with the count',
    hidden: true,
  },
];

const STOP_HOOK_SHOTS = [
  {
    assetKey: 'screenshotReviewFindings' as const,
    caption: 'Five findings, caught before the PR',
  },
  {
    assetKey: 'screenshotFlakyTestFix' as const,
    caption: 'A hanging test, fixed with a timeout',
  },
  {
    assetKey: 'screenshotReviewVerified' as const,
    caption: 'Five fixes, verification passed',
  },
];

function HomeContent() {
  const navigate = useNavigate();
  const { installation_id } = Route.useSearch();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
    if (installation_id) {
      navigate({ to: '/onboarding' });
      return;
    }
  }, [installation_id, navigate]);

  const showLoading = !mounted;

  return (
    <MarketingLayout isLoading={showLoading} isSignedIn={false}>
      <HeroSection isLoading={showLoading} />
      <ProblemSection isLoading={showLoading} />
      <BuiltForAISection isLoading={showLoading} />
      <OutcomeSection isLoading={showLoading} />
      <HowItWorksSection isLoading={showLoading} />
      <ReviewSection isLoading={showLoading} />
      <LanguagesSection isLoading={showLoading} />
      <FeatureShotsSection
        isLoading={showLoading}
        intro="One reviewer, three surfaces. Pick any or all of them."
        title="Reviewed before you merge."
        lead="Install the GitHub App for your organization, invite your team, and open a pull request. Mindrealm posts a check run with every finding annotated on the exact line. No CI config to write."
        shots={GITHUB_PR_SHOTS}
      />
      <FeatureShotsSection
        isLoading={showLoading}
        title="Findings get fixed by your agent before a PR is opened."
        lead="Wire Mindrealm into Claude Code, Codex, Kimi Code, or Antigravity CLI. When your agent finishes, Mindrealm reviews the output and feeds the findings back. The agent fixes them, then Mindrealm checks again. Or run a review yourself from the terminal, on the whole repo or just your changes."
        shots={STOP_HOOK_SHOTS}
      />
      <PhilosophySection isLoading={showLoading} />
      <AudienceSection isLoading={showLoading} />
      <OriginSection isLoading={showLoading} />
      <ConsultingTeaserSection isLoading={showLoading} />
      <PricingTeaserSection isLoading={showLoading} />
      <FinalCTASection isLoading={showLoading} />
    </MarketingLayout>
  );
}

function Home() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <HeroSection isLoading={true} />
        </MarketingLayout>
      }
    >
      <HomeContent />
    </Suspense>
  );
}
